import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { AssistantBlockProps } from './AssistantBlock.model'
import { isAssistant, userToSelectOptions } from '../../../../misc/user.utilities'
import { DropdownUserItem } from '../../DropdownUserItem'
import { FormBlock } from '../../../shared'
import { SelectOption } from '../../../../model/SelectOption'
import { LightAssistantWithPermissions, LightUserAssistant } from '../../../../model/User'
import { UserSearchIdentityCardsBlock } from '../UserSearchIdentityCardsBlock'
import { useGetInfiniteUsers } from '../../../../hooks/queries/admin'
import { Roles } from '../../../../model/Roles'
import { useAtom } from 'jotai'
import { assistantPermissionsForm } from '../../../../state/admin'
import { RESET } from 'jotai/utils'
import { searchLimit } from '../../../../constants'
import styles from './AssistantBlock.module.scss'

export const AssistantBlock: FunctionComponent<AssistantBlockProps> = ({
  openAssistantPermissions,
  initialValue,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [assistantsField, setAssistantsField] = useAtom(assistantPermissionsForm)

  useEffect(() => {
    setAssistantsField(initialValue)

    return () => {
      setAssistantsField(RESET)
    }
  }, [initialValue, setAssistantsField])

  const { userList, cancelPendingQuery } = useGetInfiniteUsers({
    filters: {
      search: inputValue,
      roles: Roles.ASSISTANT,
    },
    limit: searchLimit,
    enabled: inputValue.length > 0,
  })

  const options = useMemo(() => {
    if (userList) {
      return userList
        .filter(
          (item) =>
            !assistantsField.some((valueItem) => item.id === valueItem.id) && isAssistant(item),
        )
        .map(userToSelectOptions) as SelectOption<LightUserAssistant>[]
    }
    return []
  }, [assistantsField, userList])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.currentTarget.value)
      cancelPendingQuery()
    },
    [cancelPendingQuery],
  )

  const handleSelect = useCallback(
    (selected: SelectOption<LightUserAssistant>) => {
      setAssistantsField([
        ...assistantsField,
        {
          ...selected.value,
          permissions: {
            insOidAccess: true,
            insiServiceAccess: true,
          },
        },
      ])

      setInputValue('')
    },
    [assistantsField, setAssistantsField],
  )

  const handleRemove = useCallback(
    (user: LightAssistantWithPermissions) => {
      const filtered = assistantsField.filter((item) => item.id !== user.id)
      setAssistantsField(filtered)
    },
    [assistantsField, setAssistantsField],
  )

  return (
    <div className={styles.root}>
      <FormBlock
        label="Assistant·e·s"
        icon="profile"
        linkLabel={assistantsField.length > 0 ? 'Configurer les accès' : undefined}
        onLinkClick={assistantsField.length > 0 ? openAssistantPermissions : undefined}
      >
        <UserSearchIdentityCardsBlock
          searchPlaceholder="Rechercher un·e assistant·e"
          searchTestId="input-assistant-search"
          searchInput={inputValue}
          onSearchInputChange={handleInputChange}
          searchResults={options}
          onSelectSearchResult={handleSelect}
          renderSearchResult={(item, isHovered) => (
            <DropdownUserItem
              selected={isHovered}
              user={item.value}
              selectedValues={assistantsField}
            />
          )}
          selectedUsers={assistantsField}
          onRemoveUser={handleRemove}
          disabled={disabled}
        />
      </FormBlock>
    </div>
  )
}
